/**
 * ConnectorManagement Page
 * Admin interface for managing MCP connector visibility
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  Snackbar,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  Settings as SettingsIcon,
  DragIndicator as DragIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useApi } from '../../hooks/useApi';
import { format } from 'date-fns';

interface Connector {
  connector_key: string;
  is_enabled: boolean;
  display_name: string;
  description: string;
  icon: string;
  display_order: number;
  is_development: boolean;
  metadata: any;
  created_at: string;
  updated_at: string;
}

interface SyncResults {
  added: string[];
  updated: string[];
  errors: Array<{ connector: string; error: string }>;
}

const ConnectorManagement: React.FC = () => {
  const api = useApi();
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncResults, setSyncResults] = useState<SyncResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [updatingConnector, setUpdatingConnector] = useState<string | null>(null);

  useEffect(() => {
    fetchConnectors();
  }, []);

  const fetchConnectors = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/connectors');
      if (response.data.success) {
        setConnectors(response.data.connectors);
      } else {
        setError('Failed to fetch connectors');
      }
    } catch (error: any) {
      console.error('Failed to fetch connectors:', error);
      setError(error.message || 'Failed to fetch connectors');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleConnector = async (connector: Connector) => {
    setUpdatingConnector(connector.connector_key);
    try {
      const response = await api.put(
        `/admin/connectors/${connector.connector_key}/visibility`,
        { is_enabled: !connector.is_enabled }
      );

      if (response.data.success) {
        // Update local state
        setConnectors(prev =>
          prev.map(c =>
            c.connector_key === connector.connector_key
              ? { ...c, is_enabled: !c.is_enabled }
              : c
          )
        );
        setSuccessMessage(
          `${connector.display_name} ${!connector.is_enabled ? 'enabled' : 'disabled'} successfully`
        );
      } else {
        setError('Failed to update connector');
      }
    } catch (error: any) {
      console.error('Failed to update connector:', error);
      setError(error.message || 'Failed to update connector');
    } finally {
      setUpdatingConnector(null);
    }
  };

  const handleSyncConnectors = async () => {
    setSyncing(true);
    try {
      const response = await api.post('/admin/connectors/sync');
      
      if (response.data.success) {
        setSyncResults(response.data.results);
        setSuccessMessage(response.data.message);
        // Refresh the connector list
        await fetchConnectors();
      } else {
        setError('Failed to sync connectors');
      }
    } catch (error: any) {
      console.error('Failed to sync connectors:', error);
      setError(error.message || 'Failed to sync connectors');
    } finally {
      setSyncing(false);
      setSyncDialogOpen(false);
    }
  };

  const formatDate = (date: string) => {
    return format(new Date(date), 'MMM d, yyyy HH:mm');
  };

  const getStatusColor = (connector: Connector) => {
    if (!connector.is_enabled) return 'default';
    if (connector.is_development) return 'warning';
    return 'success';
  };

  const getStatusLabel = (connector: Connector) => {
    if (!connector.is_enabled) return 'Disabled';
    if (connector.is_development) return 'Development';
    return 'Enabled';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box mb={4}>
        <Typography variant="h4" gutterBottom fontWeight={700}>
          Connector Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Control which MCP connectors are available to users
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Sync Results Alert */}
      {syncResults && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          onClose={() => setSyncResults(null)}
        >
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Sync Results:
          </Typography>
          {syncResults.added.length > 0 && (
            <Typography variant="body2">
              Added: {syncResults.added.join(', ')}
            </Typography>
          )}
          {syncResults.updated.length > 0 && (
            <Typography variant="body2">
              Updated: {syncResults.updated.join(', ')}
            </Typography>
          )}
          {syncResults.errors.length > 0 && (
            <Typography variant="body2" color="error">
              Errors: {syncResults.errors.map(e => `${e.connector}: ${e.error}`).join(', ')}
            </Typography>
          )}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" fontWeight={600}>
            Available Connectors
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchConnectors}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<SyncIcon />}
              onClick={() => setSyncDialogOpen(true)}
              disabled={syncing}
            >
              Sync from Config
            </Button>
          </Stack>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="40px"></TableCell>
                <TableCell>Connector</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Visibility</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {connectors.map((connector) => (
                <TableRow key={connector.connector_key}>
                  <TableCell>
                    <Typography variant="h6">{connector.icon}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack>
                      <Typography variant="body1" fontWeight={600}>
                        {connector.display_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {connector.connector_key}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {connector.description}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Chip
                        label={getStatusLabel(connector)}
                        color={getStatusColor(connector) as any}
                        size="small"
                      />
                      {connector.is_development && (
                        <Tooltip title="This connector is still under development">
                          <WarningIcon color="warning" fontSize="small" />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={connector.is_enabled}
                      onChange={() => handleToggleConnector(connector)}
                      disabled={updatingConnector === connector.connector_key}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(connector.updated_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Connector details">
                      <IconButton size="small">
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {connectors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No connectors found. Click "Sync from Config" to import connectors.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Info Box */}
        <Box mt={3} p={2} bgcolor="info.lighter" borderRadius={1}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <InfoIcon color="info" fontSize="small" />
            <Box>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                About Connector Management
              </Typography>
              <Typography variant="caption" color="text.secondary">
                • Disabled connectors are hidden from regular users but visible to admins
                <br />
                • Development connectors should remain disabled until fully tested
                <br />
                • Use "Sync from Config" to import new connectors from the configuration file
                <br />
                • Changes take effect immediately for all users
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Paper>

      {/* Sync Confirmation Dialog */}
      <Dialog
        open={syncDialogOpen}
        onClose={() => setSyncDialogOpen(false)}
        aria-labelledby="sync-dialog-title"
      >
        <DialogTitle id="sync-dialog-title">
          Sync Connectors from Configuration?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will import any new connectors from the configuration file and update
            metadata for existing ones. Your visibility settings will be preserved.
            <br /><br />
            New connectors will be disabled by default for safety.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)} disabled={syncing}>
            Cancel
          </Button>
          <Button
            onClick={handleSyncConnectors}
            variant="contained"
            disabled={syncing}
            startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
          >
            {syncing ? 'Syncing...' : 'Sync Now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};

export default ConnectorManagement;